import React from "react";
import { ValidationError } from "../types";

export const describeValidationError = (
  error: ValidationError,
  onOpenRecord?: (id: string) => void,
): React.ReactNode => {
  const id = error.existingPublicId;
  const link =
    id && onOpenRecord ? (
      <button
        type="button"
        className="precheck-link"
        onClick={() => onOpenRecord(id)}
        style={{
          background: "none",
          border: 0,
          padding: 0,
          font: "inherit",
          fontWeight: 600,
          color: "var(--danger)",
          textDecoration: "underline",
          cursor: "pointer",
        }}
      >
        {id}
      </button>
    ) : (
      <strong>{id}</strong>
    );

  switch (error.code) {
    case "file_duplicate":
      return (
        <>
          This exact file was already uploaded as {link}
          {error.existingUploadedBy ? (
            <>
              {" "}
              by <strong>{error.existingUploadedBy}</strong>
            </>
          ) : null}
          {error.existingUploadedAt ? (
            <> on {error.existingUploadedAt}</>
          ) : null}
          . Use that one, or upload a different file.
        </>
      );
    case "sheet_duplicate":
      return (
        <>
          The <strong>{error.sheetName}</strong> sheet matches what was already
          submitted in {link}. The same data can&rsquo;t be reconciled twice.
        </>
      );
    case "already_reconciled":
      return (
        <>
          <strong>{error.productName}</strong> for{" "}
          <strong>{error.valueDate}</strong> is already reconciled in {link}.
          Cancel or reverse that upload first if it needs to change.
        </>
      );
    case "inconsistent_value_dates":
      return (
        <>
          The <strong>{error.sheetName}</strong> sheet has rows with different
          value dates ({(error.foundValueDates || []).join(", ")}). Every row in
          a sheet must share one value date. Fix the file and try again.
        </>
      );
    case "unknown_fund_code":
      return (
        <>
          The sheet <strong>{error.fundCode}</strong> isn&rsquo;t a fund code we
          recognise. Check that the workbook&rsquo;s sheet tab names match the
          fund codes in Phoenix&rsquo;s Products list, then try again.
        </>
      );
    case "share_class_sheet":
      return (
        <>
          The <strong>{error.sheetName}</strong> sheet is a share class of{" "}
          <strong>{error.parentName}</strong>. Share classes are reconciled
          under their parent, so put these rows on the {error.parentName} sheet
          ({error.parentFundCode}) and try again.
        </>
      );
    case "shape_mismatch":
      return (
        <>
          The replacement contains different products or value dates than the
          original. To change scope, cancel this upload and start fresh.
        </>
      );
    case "empty_sheet":
      return (
        <>
          The sheet <strong>{error.sheetName || "(unnamed)"}</strong>{" "}
          doesn&rsquo;t have any contribution rows. Add at least one row beneath
          the header before uploading.
        </>
      );
    case "invalid_drcr":
      return (
        <>
          One or more rows on <strong>{error.sheetName || "a sheet"}</strong>{" "}
          have a <code>drcr</code> value we don&rsquo;t recognise. Use{" "}
          <strong>C</strong> or <strong>Credit</strong> — payment uploads only
          accept credits.
        </>
      );
    case "debit_not_allowed":
      return (
        <>
          One or more rows on <strong>{error.sheetName || "a sheet"}</strong>{" "}
          are marked as debits. Payment uploads only accept credits — remove or
          fix those rows and re-upload.
        </>
      );
    case "unparseable_date":
      return (
        <>
          A date on <strong>{error.sheetName || "a sheet"}</strong> isn&rsquo;t
          in a format we can read. Use <code>YYYY-MM-DD</code>,{" "}
          <code>DD/MM/YYYY</code>, or <code>DD-Mmm-YYYY</code>.
        </>
      );
    case "non_working_value_date":
      return (
        <>
          The <strong>{error.sheetName}</strong> sheet has a value date of{" "}
          <strong>{error.valueDate}</strong>, which is{" "}
          {error.holidayName ? (
            <>a public holiday ({error.holidayName})</>
          ) : (
            <>a {error.weekday}</>
          )}
          . Unit prices only exist for working days, so these rows can&rsquo;t
          be reconciled. Change the value date to a working day and upload the
          file again.
        </>
      );
    case "unparseable_amount":
      return (
        <>
          A transaction amount on{" "}
          <strong>{error.sheetName || "a sheet"}</strong> isn&rsquo;t a number
          we can read. Make sure it&rsquo;s positive and uses digits with an
          optional decimal point.
        </>
      );
    case "missing_units_column":
      return (
        <>
          This sheet has no <code>units</code> column. Add it and upload the
          file again.
        </>
      );
    case "unparseable_units":
      return (
        <>
          The units value on this row couldn’t be read as a number. Check it and
          upload the file again.
        </>
      );
    case "byte_size_mismatch":
      return <>The file changed mid-upload. Pick it again and retry.</>;
    case "parse_failed":
    default:
      // If the caller supplied an explicit user-facing message (e.g. wrong
      // extension, oversized file, permission denied), surface it verbatim
      // instead of falling back to the generic "we couldn't read this file."
      if (error.message && error.message !== "parse_failed") {
        return <>{error.message}</>;
      }
      return (
        <>
          We couldn&rsquo;t read this file. It may be corrupted, password-
          protected, or in an unexpected format. Try saving it again from Excel
          as <code>.xlsx</code>.
        </>
      );
  }
};
